import { create } from 'zustand';

/**
 * Feature visibility store: controls which sections and pages the menu shows.
 * This does NOT override security; hidden features are still accessible via
 * direct URL if the user has the security level. It only controls navigation
 * visibility (navigation/navConfig.ts applies it).
 *
 * Keys are routes: a page is hidden by its route, a whole section by its
 * overview route (`/section/menus`, `/purchasing`).
 *
 * Model: we persist the set of routes the user has explicitly HIDDEN, seeded
 * from DEFAULT_HIDDEN_ROUTES below (empty = everything shown). Users manage
 * this from the Feature Access dialog (Help → About → Show All Features).
 *
 * This flipped from the older "visible set" model, whose restrictive default
 * (only Selective Touch / Kitchen) hid most of the app until users opted in.
 * Legacy saved sets from that model (v1 bare array, v2 { routes }) encoded a
 * *visible* set and are ignored on load, so those users also pick up the
 * current default. Going forward we store the user's explicit hides.
 */

/**
 * Routes hidden by default.
 *
 * Since 2026-10-26 (Jeff: "most things should" show) every menu entry shows out of the box; the
 * role, the facility's settings and its licences already decide what a user can open, so Feature
 * Access is only for someone who wants a shorter menu.
 *
 * Note this only affects users with no saved preference. Saved sets from before this change are
 * cleaned once on load (RETIRED_DEFAULT_KEYS below).
 */
const DEFAULT_HIDDEN_ROUTES = new Set<string>();

/**
 * Keys the older defaults hid. A saved v3 set copied the default it was seeded from, so these are
 * in it whether or not the user ever chose to hide them, and we cannot tell the two apart. They are
 * dropped once, when a v3 set is loaded, so existing users see Workflows, Surveys, System Log,
 * Purchasing (where licensed) and the Menu Cycle and Clinical Nutrition dashboard buttons. Every
 * other hide the user made (a page, or a whole section) is kept. The hub keys no longer name a menu
 * entry; only the dashboard's quick actions read them.
 */
const RETIRED_DEFAULT_KEYS = [
  '/menucycle-hub',
  '/clinical-nutrition',
  '/food-data',
  '/operations',
  '/workflows',
  '/surveys',
  '/purchasing',
  '/system-log',
];

/**
 * Storage schema version. v4 persists { v, hidden: string[] } (the hidden-set model). v3 is the same
 * shape, loaded once without RETIRED_DEFAULT_KEYS and saved back as v4. v1/v2 persisted a *visible*
 * set under the old restrictive default and are intentionally ignored on load.
 */
const STORAGE_VERSION = 4;

interface FeatureVisibilityState {
  /** Set of routes the user has explicitly hidden. Empty ≈ everything visible. */
  hiddenRoutes: Set<string>;
  /** Check if a route is visible (i.e. not hidden) */
  isVisible: (route: string) => boolean;
  /** Toggle a route's visibility */
  toggleRoute: (route: string) => void;
  /** Show all routes (clear all hides) */
  showAll: () => void;
  /** Hide all of the given routes */
  hideAll: (routes: string[]) => void;
  /** Sync visibility from localStorage for a given user */
  syncWithUser: (userId: number, facilityId: number) => void;
}

function storageKey(userId: number, facilityId: number) {
  return `dietech_feature_visibility_${userId}_${facilityId}`;
}

let currentUserId = 0;
let currentFacilityId = 0;

function persist(hidden: Set<string>) {
  if (!currentUserId || !currentFacilityId) return;
  try {
    localStorage.setItem(
      storageKey(currentUserId, currentFacilityId),
      JSON.stringify({ v: STORAGE_VERSION, hidden: [...hidden] })
    );
  } catch { /* ignore */ }
}

function loadFromStorage(userId: number, facilityId: number): Set<string> | null {
  try {
    const raw = localStorage.getItem(storageKey(userId, facilityId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.hidden)) return null;
    if (parsed.v === STORAGE_VERSION) return new Set(parsed.hidden as string[]);
    // v3: drop the keys the old defaults hid, once, and save the result as v4.
    if (parsed.v === 3) {
      const retired = new Set(RETIRED_DEFAULT_KEYS);
      const hidden = new Set((parsed.hidden as string[]).filter((key) => !retired.has(key)));
      localStorage.setItem(
        storageKey(userId, facilityId),
        JSON.stringify({ v: STORAGE_VERSION, hidden: [...hidden] }),
      );
      return hidden;
    }
    // Legacy formats (v1 bare array, v2 { routes }) encoded a *visible* set under the old
    // restrictive default; ignore them so those users pick up the current default.
  } catch { /* ignore */ }
  return null;
}

export const useFeatureVisibilityStore = create<FeatureVisibilityState>((set, get) => ({
  hiddenRoutes: new Set(DEFAULT_HIDDEN_ROUTES),

  isVisible: (route: string) => !get().hiddenRoutes.has(route),

  toggleRoute: (route: string) => {
    const next = new Set(get().hiddenRoutes);
    if (next.has(route)) {
      next.delete(route); // un-hide → visible
    } else {
      next.add(route); // hide
    }
    persist(next);
    set({ hiddenRoutes: next });
  },

  showAll: () => {
    const next = new Set<string>();
    persist(next);
    set({ hiddenRoutes: next });
  },

  hideAll: (routes: string[]) => {
    const next = new Set(routes);
    persist(next);
    set({ hiddenRoutes: next });
  },

  syncWithUser: (userId: number, facilityId: number) => {
    currentUserId = userId;
    currentFacilityId = facilityId;
    const saved = loadFromStorage(userId, facilityId);
    // No saved (or legacy-only) prefs → the core-menu default above.
    set({ hiddenRoutes: saved ?? new Set(DEFAULT_HIDDEN_ROUTES) });
  },
}));
